<script setup lang="ts">
import { inject, ref, computed, type HTMLAttributes } from "vue";
import { cn } from "@/lib/utils";
import { Skeleton } from "../skeleton";
import TableCell from "./TableCell.vue";
import { TABLE_COLUMN_COUNT_KEY, TABLE_IS_FETCHING_KEY } from "@/types/symbols";

const props = withDefaults(
  defineProps<{
    class?: HTMLAttributes["class"];
    isLoading?: boolean | null;
    skeletonHeight?: string;
  }>(),
  { class: undefined, isLoading: null, skeletonHeight: "h-4" },
);

// Injects the number of columns from the table component
const columns = inject(TABLE_COLUMN_COUNT_KEY);

// Injects the is fetching flag from the query
const isFetching = inject(TABLE_IS_FETCHING_KEY, ref(false));

// Determines if loading skeleton should be shown
// - isLoading=true → show loading
// - isLoading=false → hide loading (overrides isFetching)
// - isLoading=null (not passed) → defer to isFetching
const showLoading = computed(() => {
  if (props.isLoading === true) return true;
  if (props.isLoading === false) return false;
  if (isFetching === undefined) return false;
  return isFetching.value;
});
</script>

<template>
  <tr
    data-slot="table-row"
    :class="cn('hover:bg-muted data-[state=selected]:bg-muted border-border border-b transition-colors', props.class)"
  >
    <template v-if="showLoading">
      <TableCell :colspan="columns ?? 1">
        <Skeleton :class="cn('my-1 w-full', props.skeletonHeight)" />
      </TableCell>
    </template>
    <template v-else>
      <slot />
    </template>
  </tr>
</template>
